import { UseQueryResult } from '@tanstack/react-query';

import { useGraphql } from '@app/hooks/useGraphql';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { GraphqlQueryDataResponse } from '@insights_module/types/graphql/ClientTypes';

export const useGraphqlAudioDetails = (audioId: string) => {
  const { data, ...rest } = useGraphql({
    queryKey: ['graphql', 'graphql-audio-details', audioId],
    graphqlQuery: audioDetailsQuery,
    variables: { audioId },
    enabled: !!audioId,
    staleTime: 60 * 60 * 1000,
  }) as { data: GraphqlQueryDataResponse } & UseQueryResult<unknown, unknown>;

  return { data: data?.data?.audio, ...rest };
};

export const useSelectedEpisodeDetails = () => {
  const { selectedEpisode } = useInsightsContext();

  return useGraphqlAudioDetails(selectedEpisode);
};

const audioDetailsQuery = `query AudioDetails($audioId: String) {
  audio(id: $audioId) {
    id
    title
    duration
    client_duration
    original_url
    hosted_url
    language
    skip_processing
    external_ids {
      name
      value
    }
    groups {
      id
      name
      type
      thumbnail_url
      tenant_id
      audio_count
      most_recently_published_audio_date
      extra_fields {
        name
        value
      }
      podcast_metadata {
        apple_categories
        apple_rating_count
        apple_star_rating
        author
        description
        podcast_type
      }
      created_at
      updated_at
    }
    published_date
    created_at
    updated_at
    gen_ai {
      twitter
      facebook
      instagram
      linkedin
      show_notes
      summary
      article
    }
    brand_safety_garm {
      adult_explicit_content
      arms_ammunition
      crime_harmful_act
      death_injury_military_conflict
      debated_social
      illegal_drugs
      hate_speech
      obscenity
      online_piracy
      spam_harmful
      terrorism
    }
    brand_safety_garm_traceability {
      adult_explicit_content
      arms_ammunition
      crime_harmful_act
      death_injury_military_conflict
      debated_social
      illegal_drugs
      hate_speech
      obscenity
      online_piracy
      spam_harmful
      terrorism
      start_time
      end_time
    }
    contextual {
      id
      name
      display_name
      score
      taxonomy {
        taxonomy
        name
        tier
        id
        parent_id
      }
    }
    contextual_data_traceability {
      categories {
        id
        name
        display_name
        score
      }
      start_time
      end_time
    }
    named_entities {
      type
      value
      timestamps
    }
    keywords {
      keyword
      score
    }
    topics {
      topic
      score
    }
    chapters {
      start_time
      end_time
      title_suggestions {
        title
        score
      }
    }
  }
}
`;
